import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { PanelSpan } from '@/components/shared';
import { fetchBackofficeParticipantDetails } from '../api';
import { formatDateTime, formatJson, formatNullable, formatStatus } from '../format';
import type { BackofficeParticipantDetails } from '../types';
import { DetailRow } from './DetailRow';
import { InterviewDetailPanel } from './InterviewDetailPanelGrid';

export interface ParticipantDetailsPanelProps {
  interviewId: string;
  panelId?: string;
  currentSpan?: PanelSpan;
}

function sourceLabel(source: BackofficeParticipantDetails['source']): string {
  const labels = {
    panel: 'Panel participant',
    participant_link: 'Participant link',
    anonymous: 'Anonymous direct link',
    preview: 'Preview',
    unknown: 'Other',
  };
  return `${labels[source.type]} (${formatStatus(source.raw)})`;
}

function lookupLabel(state: BackofficeParticipantDetails['assignment']['state']): string {
  if (state === 'missing') return 'Not found';
  if (state === 'not_applicable') return 'Not applicable';
  if (state === 'unavailable') return 'Unavailable';
  return 'Available';
}

function providerValue(data: Record<string, unknown> | null, key: string): string {
  const value = data?.[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : 'Not available';
}

export function ParticipantDetailsPanel({
  interviewId,
  panelId,
  currentSpan,
}: ParticipantDetailsPanelProps) {
  const query = useQuery({
    queryKey: ['backoffice', 'interview-participant-details', interviewId],
    queryFn: ({ signal }) => fetchBackofficeParticipantDetails(interviewId, signal),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const retryButton = (
    <button
      type="button"
      onClick={() => void query.refetch()}
      className="focus-ring inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-800"
      aria-label="Retry participant details"
    >
      <RefreshCw
        className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      Retry
    </button>
  );

  if (query.isPending) {
    return (
      <InterviewDetailPanel
        key="loading"
        title="Participant details"
        span="2x5"
        panelId={panelId}
        currentSpan={currentSpan}
      >
        <p className="text-body text-neutral-500">Loading participant and panel details</p>
      </InterviewDetailPanel>
    );
  }

  if (query.isError) {
    return (
      <InterviewDetailPanel
        key="error"
        title="Participant details"
        span="2x5"
        panelId={panelId}
        currentSpan={currentSpan}
        headerActions={retryButton}
      >
        <div className="rounded-md bg-red-50 p-3 text-body text-red-800">
          Participant details could not be loaded. Use Retry to try again.
        </div>
      </InterviewDetailPanel>
    );
  }

  const details = query.data.participantDetails;
  const assignment = details.assignment.data;
  const panel = details.panelSubmission.data;
  const providerStatus =
    providerValue(details.provider.data, 'statusLabel') !== 'Not available'
      ? `${providerValue(details.provider.data, 'statusLabel')} (${providerValue(details.provider.data, 'status')})`
      : providerValue(details.provider.data, 'status');
  const comparisonTone =
    details.comparison.state === 'mismatch'
      ? 'border-red-200 bg-red-50 text-red-800'
      : details.comparison.state === 'aligned'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-neutral-200 bg-neutral-50 text-neutral-700';

  const detailItems = [
    {
      id: 'status-comparison',
      label: 'Status comparison',
      content: (
        <div
          key="status-comparison"
          className={`mb-2 rounded-md border p-3 text-body ${comparisonTone}`}
          role={details.comparison.state === 'mismatch' ? 'alert' : undefined}
        >
          <span className="font-semibold">
            {details.comparison.state.charAt(0).toUpperCase() + details.comparison.state.slice(1)}
          </span>
          <span> — {details.comparison.reason}</span>
        </div>
      ),
    },
    {
      id: 'source',
      label: 'Source',
      content: <DetailRow key="source" label="Source" value={sourceLabel(details.source)} />,
    },
    {
      id: 'participant-id',
      label: 'Participant ID',
      content: (
        <DetailRow
          key="participant-id"
          label="Participant ID"
          value={formatNullable(details.participant.id)}
          {...(details.participant.id
            ? { copyValue: details.participant.id, copyLabel: 'Copy participant ID' }
            : {})}
        />
      ),
    },
    {
      id: 'collected-email',
      label: 'Collected email',
      content: (
        <DetailRow
          key="collected-email"
          label="Collected email"
          value={details.participantEmail.email ?? lookupLabel(details.participantEmail.state)}
          {...(details.participantEmail.email
            ? {
                copyValue: details.participantEmail.email,
                copyLabel: 'Copy collected participant email',
              }
            : {})}
        />
      ),
    },
    {
      id: 'respondent-contact',
      label: 'Respondent contact',
      content: (
        <DetailRow
          key="respondent-contact"
          label="Respondent contact"
          value={formatNullable(assignment?.displayIdentifier)}
          {...(assignment?.displayIdentifier
            ? { copyValue: assignment.displayIdentifier, copyLabel: 'Copy respondent contact' }
            : {})}
        />
      ),
    },
    {
      id: 'assignment-status',
      label: 'Assignment',
      content: (
        <DetailRow
          key="assignment-status"
          label="Assignment"
          value={
            assignment ? formatStatus(assignment.status) : lookupLabel(details.assignment.state)
          }
        />
      ),
    },
    {
      id: 'attempt-status',
      label: 'Attempt status',
      content: (
        <DetailRow
          key="attempt-status"
          label="Attempt status"
          value={assignment ? formatStatus(assignment.attemptStatus) : 'Not available'}
        />
      ),
    },
    {
      id: 'participant-metadata',
      label: 'Participant metadata',
      content: (
        <DetailRow
          key="participant-metadata"
          label="Participant metadata"
          value={assignment ? formatJson(assignment.metadata) : 'Not available'}
        />
      ),
    },
    {
      id: 'nava-panel-status',
      label: 'Nava panel status',
      content: (
        <DetailRow
          key="nava-panel-status"
          label="Nava panel status"
          value={panel ? formatStatus(panel.status) : lookupLabel(details.panelSubmission.state)}
        />
      ),
    },
    {
      id: 'provider-status',
      label: 'Provider status',
      content: (
        <DetailRow
          key="provider-status"
          label={`${details.provider.kind ? formatStatus(details.provider.kind) : 'Provider'} status`}
          value={
            details.provider.state === 'available'
              ? providerStatus
              : lookupLabel(details.provider.state)
          }
        />
      ),
    },
    {
      id: 'provider-submission-id',
      label: 'Provider submission ID',
      content: (
        <DetailRow
          key="provider-submission-id"
          label="Provider submission ID"
          value={formatNullable(
            panel?.providerSubmissionId ?? providerValue(details.provider.data, 'id')
          )}
        />
      ),
    },
    {
      id: 'provider-participant-id',
      label: 'Provider participant ID',
      content: (
        <DetailRow
          key="provider-participant-id"
          label="Provider participant ID"
          value={formatNullable(
            panel?.providerParticipantId ?? details.participant.prolificParticipantId
          )}
        />
      ),
    },
    {
      id: 'quality-score',
      label: 'Panel quality score',
      content: (
        <DetailRow
          key="quality-score"
          label="Panel quality score"
          value={formatNullable(panel?.qualityScore)}
        />
      ),
    },
    {
      id: 'panel-completed-at',
      label: 'Panel completed',
      content: (
        <DetailRow
          key="panel-completed-at"
          label="Panel completed"
          value={formatDateTime(panel?.completedAt ?? null)}
        />
      ),
    },
  ];

  return (
    <InterviewDetailPanel
      key="loaded"
      title="Participant details"
      span="2x5"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
      headerActions={details.provider.state === 'unavailable' ? retryButton : undefined}
    />
  );
}
